// Pages/backup/backupUtils.ts
//
// Lógica PURA de /backup (sin React, sin red): el nombre del ZIP según se
// incluyan o no los medios y los textos del control «Incluir fotos, videos y
// documentos». Vive aparte para testearla sin montar la página.
//
// Backup con o sin medios (round 2026-10-27). Ver docs/modulo-backup.md §3.bis.
import type { DesgloseMedios, EstimacionMedios } from "../../services/backup.api";
import { formatPeso } from "../almacenamiento/almacenamientoUtils";

/** Estado de la carga de la estimación del peso de los medios. */
export type EstadoEstimacion = "cargando" | "lista" | "no_disponible";

/**
 * Nombre del ZIP descargado. Distingue la variante sin medios para no
 * confundirla con una copia total al revisar la carpeta de descargas. Es el
 * mismo nombre que arma el backend en `Content-Disposition`.
 */
export function nombreBackupZip(fecha: string, incluirMedios: boolean): string {
  return `backup-${fecha}${incluirMedios ? "" : "-sin-medios"}.zip`;
}

/** Singular / plural de cada clase de archivo del volumen, en el orden en que se muestran. */
const CLASES: Array<{
  clave: keyof DesgloseMedios;
  singular: string;
  plural: string;
}> = [
  { clave: "originales", singular: "original", plural: "originales" },
  { clave: "miniaturas", singular: "miniatura", plural: "miniaturas" },
  { clave: "livianas", singular: "versión liviana", plural: "versiones livianas" },
  { clave: "otros", singular: "otro archivo", plural: "otros archivos" },
];

/** `1 archivo` / `24 archivos`. */
export function textoArchivos(n: number): string {
  return `${n} ${n === 1 ? "archivo" : "archivos"}`;
}

/**
 * El desglose por clase, solo con las que tienen algo:
 * `11 originales + 10 miniaturas + 3 versiones livianas`. Es el MISMO vocabulario con
 * que el servidor declara el contenido del ZIP (`BACKUP_INFO.json`) y con que informa
 * lo restaurado. `null` si no hay desglose o está vacío.
 */
export function textoDesgloseMedios(desglose: DesgloseMedios | null | undefined): string | null {
  if (!desglose) return null;
  const partes = CLASES.flatMap(({ clave, singular, plural }) => {
    const n = Number(desglose[clave]?.archivos);
    return Number.isFinite(n) && n > 0 ? [`${n} ${n === 1 ? singular : plural}`] : [];
  });
  return partes.length > 0 ? partes.join(" + ") : null;
}

/**
 * Lo que se le muestra al operador junto al interruptor: exactamente qué trae el ZIP.
 *
 * `11 originales + 10 miniaturas + 3 versiones livianas = 24 archivos · 23,2 MB`.
 * Con una sola clase no repite la cuenta (`3 archivos · 1 MB`). Es lo que el servidor
 * medirá en el volumen al armar el ZIP (mismo recorrido), no una aproximación: la
 * única diferencia con el peso del archivo descargado es la compresión, casi nula en
 * fotos y videos, y los CSV de las tablas (pocos MB).
 */
export function textoPesoMedios(
  estimacion: EstimacionMedios | null,
  estado: EstadoEstimacion,
): string {
  if (estado === "cargando") return "Calculando el peso…";
  if (estado === "no_disponible" || !estimacion) {
    return "No se pudo calcular el peso. La descarga funciona igual.";
  }
  if (estimacion.archivosMedios === 0) {
    return "No hay archivos en el volumen (ni fotos, videos, documentos, miniaturas ni versiones livianas).";
  }
  const total = `${textoArchivos(estimacion.archivosMedios)} · ${formatPeso(estimacion.bytesMedios)}`;
  const desglose = textoDesgloseMedios(estimacion.desglose);
  // Con una sola clase, «3 originales = 3 archivos» sería repetir lo mismo.
  const variasClases =
    desglose != null && CLASES.filter((c) => (estimacion.desglose?.[c.clave]?.archivos ?? 0) > 0).length > 1;
  return variasClases ? `Incluye ${desglose} = ${total}.` : `Incluye ${total}.`;
}

/**
 * Lo restaurado, con los mismos conceptos: `11 originales + 10 miniaturas + 3 versiones
 * livianas`. `null` si el servidor no lo informó (versión anterior) o no se restauró nada.
 */
export function textoDesgloseRestaurado(
  desglose: DesgloseMedios | null | undefined,
  restaurados: number | null | undefined,
): string | null {
  const texto = textoDesgloseMedios(desglose);
  if (!texto || !restaurados) return null;
  return `${texto} = ${textoArchivos(restaurados)}`;
}

/**
 * «Se cambió el rol de 3 cuentas»: lo que dice la página cuando el respaldo que se restauró o se importó dejó a usuarios que ya existían con OTRO
 * rol (B3d). `null` si nadie cambió (o el servidor no lo informó): no hay nada que avisar.
 */
export function textoRolesCambiados(cantidad: number | null | undefined): string | null {
  if (typeof cantidad !== "number" || !Number.isInteger(cantidad) || cantidad <= 0) return null;
  return `Se cambió el rol de ${cantidad} ${cantidad === 1 ? "cuenta" : "cuentas"}`;
}

/** «Quedan 2 superadministradores»: cuántos hay después de restaurar o importar. `null` si el servidor no lo informó. */
export function textoSuperadministradores(cantidad: number | null | undefined): string | null {
  if (typeof cantidad !== "number" || !Number.isInteger(cantidad) || cantidad < 0) return null;
  return `Quedan ${cantidad} ${cantidad === 1 ? "superadministrador" : "superadministradores"}`;
}

/** La nota corta que explica cuándo conviene cada opción. */
export const NOTA_SIN_MEDIOS =
  "Sin medios el respaldo es rápido y liviano: ideal para copias frecuentes de la base de datos.";
export const NOTA_CON_MEDIOS =
  "Con medios es la copia total, para recuperarse de un desastre: originales, miniaturas y versiones livianas. Con muchos videos puede ser muy pesada.";
